(() => {
  let clientInstance = null;
  const MAX_REVIEW_PHOTOS = 5;

  const config = () => window.RUBYST_SUPABASE_CONFIG || {};
  const configured = () => {
    const { url, publishableKey } = config();
    return Boolean(url && /^https:\/\//i.test(url) && publishableKey && !publishableKey.includes('PASTE'));
  };

  const client = () => {
    if (!configured()) throw new Error('Review service is not configured yet.');
    if (!window.supabase?.createClient) throw new Error('Review service failed to load. Please try again.');
    if (!clientInstance) {
      const { url, publishableKey } = config();
      clientInstance = window.supabase.createClient(url, publishableKey, {
        auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true },
      });
    }
    return clientInstance;
  };

  const clean = (value, limit = 1200) => String(value || '').trim().slice(0, limit);

  const normalizeReviewPhotoUrls = (...values) => {
    const result = [];
    const append = (value) => {
      if (Array.isArray(value)) {
        value.forEach(append);
        return;
      }
      const url = clean(value, 2000);
      if (url && !result.includes(url)) result.push(url);
    };
    values.forEach(append);
    return result.slice(0, MAX_REVIEW_PHOTOS);
  };

  const uploadReviewPhoto = async (file) => {
    if (!(file instanceof File) || !file.size) return '';
    if (!file.type.startsWith('image/') || file.size > 1024 * 1024) {
      throw new Error('Review photos must be an image smaller than 1 MB.');
    }
    const extension = (file.name.split('.').pop() || 'jpg').replace(/[^a-z0-9]/gi, '').slice(0, 5) || 'jpg';
    const path = `pending/${new Date().toISOString().slice(0, 10)}/${crypto.randomUUID()}.${extension}`;
    const api = client();
    const { error } = await api.storage.from('rubyst-review-media').upload(path, file, {
      cacheControl: '31536000', upsert: false, contentType: file.type,
    });
    if (error) throw error;
    return api.storage.from('rubyst-review-media').getPublicUrl(path).data.publicUrl;
  };

  const uploadReviewPhotos = async (files) => {
    const selected = Array.from(files || []).filter((file) => file instanceof File && file.size);
    if (selected.length > MAX_REVIEW_PHOTOS) {
      throw new Error(`You can upload up to ${MAX_REVIEW_PHOTOS} review photos.`);
    }
    return Promise.all(selected.map(uploadReviewPhoto));
  };

  const getApprovedReviews = async (productId) => {
    const { data, error } = await client()
      .from('rubyst_reviews')
      .select('id,product_id,customer_name,rating,title,body,photo_url,photo_urls,source,created_at')
      .eq('product_id', clean(productId, 80))
      .eq('status', 'approved')
      .order('created_at', { ascending: false });
    if (error) throw error;
    return (data || []).map((review) => {
      const photos = normalizeReviewPhotoUrls(review.photo_urls, review.photo_url);
      return {
        id: review.id,
        name: review.customer_name,
        rating: review.rating,
        title: review.title,
        body: review.body,
        photo: photos[0] || '',
        photos,
        source: review.source || 'Website',
        createdAt: review.created_at,
      };
    });
  };

  const getReviewSummary = async (productId) => {
    const { data, error } = await client()
      .from('rubyst_review_summaries')
      .select('product_id,rating,review_count,updated_at')
      .eq('product_id', clean(productId, 80))
      .maybeSingle();
    if (error) throw error;
    if (!data) return null;
    return {
      productId: data.product_id,
      rating: Math.max(1, Math.min(5, Number(data.rating) || 4.8)),
      reviewCount: Math.max(0, Math.round(Number(data.review_count) || 0)),
      updatedAt: data.updated_at,
    };
  };

  const submitReview = async ({ productId, name, rating, title, body, photoUrl, photoFile, photoFiles }) => {
    const selectedFiles = photoFiles ? Array.from(photoFiles) : [photoFile].filter(Boolean);
    const externalPhotoUrl = clean(photoUrl, 2000);
    if (selectedFiles.filter((file) => file instanceof File && file.size).length + (externalPhotoUrl ? 1 : 0) > MAX_REVIEW_PHOTOS) {
      throw new Error(`A review can contain up to ${MAX_REVIEW_PHOTOS} photos in total.`);
    }
    const uploadedPhotoUrls = await uploadReviewPhotos(selectedFiles);
    const finalPhotoUrls = normalizeReviewPhotoUrls(externalPhotoUrl, uploadedPhotoUrls);
    /* Customer review time is deliberately generated by the database. The
       date-only fallback keeps the legacy, required title column compatible
       without exposing an editable review title in the storefront. */
    const automaticTitle = new Date().toISOString().slice(0, 10);
    const payload = {
      product_id: clean(productId, 80),
      customer_name: clean(name, 60),
      rating: Math.max(1, Math.min(5, Number(rating) || 5)),
      title: clean(title, 120) || automaticTitle,
      body: clean(body, 1200),
      photo_url: finalPhotoUrls[0] || null,
      photo_urls: finalPhotoUrls,
      source: 'Website',
      status: 'approved',
    };
    const { error } = await client().from('rubyst_reviews').insert(payload);
    if (error) throw error;
  };

  const submitForm = async (formType, values = {}) => {
    const payload = {
      form_type: formType,
      email: clean(values.email, 254) || null,
      contact_channel: clean(values.contactChannel, 40) || null,
      contact_value: clean(values.contactValue, 254) || null,
      street: clean(values.street, 180) || null,
      city: clean(values.city, 100) || null,
      state: clean(values.state, 40) || null,
      zip: clean(values.zip, 20) || null,
      page_url: window.location.href.slice(0, 2000),
      metadata: { language: document.documentElement.lang || 'en', referrer: document.referrer.slice(0, 2000) },
    };
    const { error } = await client().from('rubyst_form_submissions').insert(payload);
    if (error) throw error;
  };

  window.RubystData = Object.freeze({
    configured,
    client,
    getApprovedReviews,
    getReviewSummary,
    submitReview,
    submitForm,
    uploadReviewPhoto,
    uploadReviewPhotos,
    normalizeReviewPhotoUrls,
    maxReviewPhotos: MAX_REVIEW_PHOTOS,
  });
  window.dispatchEvent(new CustomEvent('rubyst:data-ready'));
})();
